// src/pages/Admin/ManageUsers.js

import React, {
  useCallback,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import { useAuth } from "../../hooks/pages/useAuth";

import { useUserManagement } from "../../hooks/admin/user/useUserManagement";

import UserTable from "../../components/admin/users/UserTable";
import UserForm from "../../components/admin/users/UserForm";

import {
  USER_INITIAL_FORM,
  USER_INITIAL_FILTERS,
} from "../../constants/admin/user/userConstants";

const ManageUsers = () => {
  const navigate = useNavigate();

  const { user: currentUser } = useAuth();

  const {
    users,

    loading,

    filters,
    setFilters,

    page,
    setPage,

    totalPages,

    create,
    update,
    remove,
  } = useUserManagement();

  const [editingUser, setEditingUser] =
    useState(null);

  const [formData, setFormData] =
    useState(USER_INITIAL_FORM);

  const [showForm, setShowForm] =
    useState(false);

  const closeModal = useCallback(() => {
    setShowForm(false);

    setEditingUser(null);

    setFormData(USER_INITIAL_FORM);
  }, []);

  const handleCreate =
    useCallback(() => {
      setEditingUser(null);

      setFormData(
        USER_INITIAL_FORM
      );

      setShowForm(true);
    }, []);

  const handleEdit = useCallback(
    (user) => {
      setEditingUser(user);

      setFormData({
        name: user.name || "",
        email: user.email || "",
        password: "",
        phone: user.phone || "",
        role: user.role || "staff",
      });

      setShowForm(true);
    },
    []
  );

  const handleDelete =
    useCallback(
      async (id) => {
        const confirmed =
          window.confirm(
            "Bạn có chắc chắn muốn xoá người dùng?"
          );

        if (!confirmed) return;

        try {
          await remove(id);

          toast.success(
            "Xóa người dùng thành công!"
          );
        } catch (error) {
          console.error(error);

          toast.error(
            "Không thể xoá người dùng"
          );
        }
      },
      [remove]
    );

  const handleChange = useCallback(
    (e) => {
      const { name, value } =
        e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleSubmit =
    useCallback(async () => {
      try {
        if (editingUser) {
          await update(
            editingUser.id,
            formData
          );

          toast.success(
            "Cập nhật người dùng thành công!"
          );
        } else {
          await create(formData);

          toast.success(
            "Tạo người dùng mới thành công!"
          );
        }

        closeModal();
      } catch (error) {
        console.error(error);

        toast.error(
          error?.response?.data?.message ||
            "Không thể lưu người dùng"
        );
      }
    }, [
      editingUser,
      formData,
      create,
      update,
      closeModal,
    ]);

  const handleFilterChange =
    useCallback(
      (e) => {
        const { name, value } =
          e.target;

        setFilters((prev) => ({
          ...prev,
          [name]: value,
        }));

        setPage(1);
      },
      [setFilters, setPage]
    );

  const handleResetFilters =
    useCallback(() => {
      setFilters(
        USER_INITIAL_FILTERS
      );

      setPage(1);
    }, [setFilters]);

  const handleViewCars =
    useCallback(
      (userId) => {
        navigate(
          `/admin/users/${userId}/cars`
        );
      },
      [navigate]
    );

  return (
    <div className="p-6 space-y-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-blue-700 dark:text-blue-400">
          Quản lý người dùng
        </h1>

        {currentUser?.role ===
          "admin" && (
          <button
            onClick={
              handleCreate
            }
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
          >
            + Thêm người dùng
          </button>
        )}
      </div>

      {/* Filter */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 bg-gray-100 dark:bg-gray-800 p-4 rounded">
        <input
          name="name"
          placeholder="Tên"
          value={
            filters.name || ""
          }
          onChange={
            handleFilterChange
          }
          className="p-2 border rounded"
        />

        <input
          name="email"
          placeholder="Email"
          value={
            filters.email || ""
          }
          onChange={
            handleFilterChange
          }
          className="p-2 border rounded"
        />

        <input
          name="phone"
          placeholder="SĐT"
          value={
            filters.phone || ""
          }
          onChange={
            handleFilterChange
          }
          className="p-2 border rounded"
        />

        <select
          name="role"
          value={
            filters.role || ""
          }
          onChange={
            handleFilterChange
          }
          className="p-2 border rounded"
        >
          <option value="">
            Tất cả vai trò
          </option>

          <option value="admin">
            Admin
          </option>

          <option value="staff">
            Staff
          </option>

          <option value="users">
            Users
          </option>
        </select>

        <button
          onClick={
            handleResetFilters
          }
          className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded"
        >
          Đặt lại
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p>
          Đang tải dữ liệu...
        </p>
      ) : (
        <UserTable
          users={users}
          onEdit={handleEdit}
          onDelete={
            handleDelete
          }
          onViewCars={
            handleViewCars
          }
        />
      )}

      {/* Pagination */}
      <div className="flex justify-center items-center gap-2">
        <button
          disabled={page === 1}
          onClick={() =>
            setPage((prev) =>
              Math.max(
                prev - 1,
                1
              )
            )
          }
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          ← Trước
        </button>

        <span>
          Trang {page} /{" "}
          {totalPages}
        </span>

        <button
          disabled={
            page === totalPages
          }
          onClick={() =>
            setPage((prev) =>
              Math.min(
                prev + 1,
                totalPages
              )
            )
          }
          className="px-3 py-1 border rounded disabled:opacity-50"
        >
          Sau →
        </button>
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto">
          <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-lg shadow-xl mx-4">
            <button
              onClick={
                closeModal
              }
              className="absolute top-3 right-4 text-2xl"
            >
              &times;
            </button>

            <div className="p-6">
              <UserForm
                formData={
                  formData
                }
                handleChange={
                  handleChange
                }
                handleSubmit={
                  handleSubmit
                }
                editingUser={
                  editingUser
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;